import React, { useState } from 'react';
import {
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Category } from '../../types';
import { useLanguage } from '../../contexts/LanguageContext';
import { categoryOptions, toggleCategory } from '../../utils/taskPresentation';
import { Segments } from './TaskControls';

export default function CategoryScope({
  categories,
  mode,
  onMode,
  value,
  onChange,
  error,
}: {
  categories: Category[];
  mode: string;
  onMode: (mode: string) => void;
  value: string[];
  onChange: (ids: string[]) => void;
  error?: string;
}) {
  const { t } = useLanguage();
  const tr = (key: string) => t('operations.' + key);
  const [search, setSearch] = useState('');
  const options = categoryOptions(categories);
  return (
    <View style={s.section}>
      <Segments
        label={tr('scope')}
        value={mode}
        onChange={onMode}
        options={[
          { id: 'all', name: tr('taskWholeStore') },
          { id: 'selected', name: tr('taskSelectedCategories') },
        ]}
      />
      <Text style={s.hint}>
        {tr(mode === 'all' ? 'taskWholeStoreHint' : 'taskCategoryHint')}
      </Text>
      {mode === 'selected' && (
        <>
          <View style={s.searchRow}>
            <Ionicons name="search" size={18} color="#64748B" />
            <TextInput
              accessibilityLabel={tr('taskSearchCategories')}
              placeholder={tr('taskSearchCategories')}
              value={search}
              onChangeText={setSearch}
              style={s.search}
            />
          </View>
          <ScrollView
            nestedScrollEnabled
            keyboardShouldPersistTaps="handled"
            style={s.list}
          >
            {options
              .filter((option) =>
                option.path
                  .toLocaleLowerCase()
                  .includes(search.trim().toLocaleLowerCase()),
              )
              .map((option) => {
                const inherited = option.ancestors.some((id) =>
                  value.includes(id),
                );
                const checked = inherited || value.includes(option.id);
                return (
                  <Pressable
                    key={option.id}
                    accessibilityRole="checkbox"
                    accessibilityLabel={option.path}
                    accessibilityState={{ checked, disabled: inherited }}
                    aria-checked={checked}
                    aria-disabled={inherited}
                    disabled={inherited}
                    onPress={() =>
                      onChange(toggleCategory(value, option.id, categories))
                    }
                    style={[
                      s.option,
                      { paddingLeft: 12 + Math.min(option.depth, 4) * 14 },
                      inherited && s.inherited,
                    ]}
                  >
                    <Ionicons
                      name={checked ? 'checkbox' : 'square-outline'}
                      size={21}
                      color={checked ? '#2563EB' : '#94A3B8'}
                    />
                    <View style={s.flex}>
                      <Text style={s.name}>{option.name}</Text>
                      {option.depth > 0 && (
                        <Text style={s.path}>{option.path}</Text>
                      )}
                    </View>
                    {inherited && (
                      <Ionicons name="link-outline" size={16} color="#64748B" />
                    )}
                  </Pressable>
                );
              })}
            {!options.some((option) =>
              option.path
                .toLocaleLowerCase()
                .includes(search.trim().toLocaleLowerCase()),
            ) && <Text style={s.empty}>{tr('taskNoResults')}</Text>}
          </ScrollView>
          {!!value.length && (
            <Text style={s.hint}>
              {tr('taskScopeCount')}: {value.length}
            </Text>
          )}
        </>
      )}
      {!!error && (
        <Text accessibilityRole="alert" style={s.error}>
          {error}
        </Text>
      )}
    </View>
  );
}
const s = StyleSheet.create({
  section: { gap: 10 },
  hint: { fontSize: 12, color: '#64748B', lineHeight: 18 },
  searchRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    borderWidth: 1,
    borderColor: '#CBD5E1',
    borderRadius: 8,
    paddingHorizontal: 10,
    backgroundColor: '#FFFFFF',
  },
  search: { flex: 1, minHeight: 44, fontSize: 14, color: '#0F172A' },
  list: {
    maxHeight: 240,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    backgroundColor: '#FFFFFF',
  },
  option: {
    flexDirection: 'row',
    gap: 10,
    alignItems: 'center',
    paddingVertical: 10,
    paddingRight: 12,
    minHeight: 44,
    borderBottomWidth: 1,
    borderColor: '#F1F5F9',
  },
  inherited: { backgroundColor: '#F8FAFC' },
  flex: { flex: 1 },
  name: { color: '#334155', fontSize: 14 },
  path: { color: '#64748B', fontSize: 11, marginTop: 2 },
  empty: { padding: 16, color: '#64748B' },
  error: { color: '#B91C1C', fontSize: 12 },
});
